import { useForm, Controller, FormProvider, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form, Input, Button, InputNumber, Select, Radio, Space, Checkbox, Card, DatePicker } from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import { useLanguage } from '../../utils/LanguageContext';
import { ServerValidationError } from '../../utils/api';
import dayjs from 'dayjs';
import { cloneElement, forwardRef, useEffect, useCallback, useState } from 'react';
import { Explainer } from './CommonComponents';
import schemaTranslations from '../../shared/validations/allTranslations.lang.js';
import { useClientParams } from '../../utils/AppConfigContext.js';

const FormField = ({ name, label, render }) => {
  const { control, infoFor, errorFor } = useFormContext();

  const info = infoFor(name);
  const error = errorFor(name);

  return (
    <>
      <Form.Item
        name={name}
        label={label || info?.label}
        required={info?.required}
        validateStatus={error ? 'error' : ''}
        help={error}
      >
        <Controller
          control={control}
          name={name}
          render={({ field }) => render({ field: { ...field, placeholder: info.placeholder } })}
        />
      </Form.Item>
      {info?.explainer && <Explainer question={info.explainer.question} answer={info.explainer.answer} />}
    </>
  );
};

const invalidType = (type) => {
  throw new Error('Invalid type specified for control: ' + JSON.stringify(type));
};

const fieldForCheckbox = ({ value, ...rest }) => ({ checked: value, ...rest });

const datePickerProps = ({ value, onChange, ...rest }) => {
  const yyymmddFormat = 'YYYY-MM-DD';

  return {
    format: yyymmddFormat,
    value: value && dayjs(value),
    onChange: (date) => onChange(date ? date.format(yyymmddFormat) : null),
    style: { width: '100%' },
    ...rest,
  };
};

const FormInput = forwardRef(({ name, label, state, type, ...props }, ref) => (
  <FormField
    name={name}
    label={label}
    render={({ field }) => {
      const handleBlur = (e) => {
        const trimmedValue = e.target.value.trim();
        field.onChange(trimmedValue || undefined);
        field.onBlur(e);
      };

      return type === 'text' ? (
        <Input {...field} {...props} ref={ref} onBlur={handleBlur} />
      ) : type === 'email' ? (
        <Input {...field} {...props} prefix={<MailOutlined className="site-form-item-icon" />} onBlur={handleBlur} />
      ) : type === 'number' ? (
        <InputNumber {...field} {...props} />
      ) : type === 'dollars' ? (
        <InputNumber {...field} addonBefore="$" {...props} />
      ) : type === 'percent' ? (
        <InputNumber {...field} addonAfter="%" {...props} />
      ) : type === 'password' ? (
        <Input.Password {...field} {...props} prefix={<LockOutlined />} />
      ) : type === 'checkbox' ? (
        <Checkbox {...fieldForCheckbox(field)} {...props} />
      ) : type === 'date' ? (
        <DatePicker {...datePickerProps(field)} {...props} />
      ) : (
        invalidType(type)
      );
    }}
  />
));

const FormChoice = ({ name, label, state, options, type, ...props }) => (
  <FormField
    name={name}
    label={label}
    render={({ field }) =>
      type === 'button' ? (
        <Radio.Group {...field} {...props}>
          <Space direction="vertical">
            {options.map(({ value, label }) => (
              <Radio.Button key={value} value={value}>
                {label}
              </Radio.Button>
            ))}
          </Space>
        </Radio.Group>
      ) : type === 'radio' ? (
        <Radio.Group {...field} {...props}>
          {options.map(({ value, label }) => (
            <Radio key={value} value={value}>
              {label}
            </Radio>
          ))}
        </Radio.Group>
      ) : type === 'select' ? (
        <Select {...field} {...props}>
          {options.map(({ value, label }) => (
            <Select.Option key={value} value={value}>
              {label}
            </Select.Option>
          ))}
        </Select>
      ) : type === 'checkboxgroup' ? (
        <Checkbox.Group {...field}>
          {options.map(({ value, label }) => (
            <Checkbox key={value} value={value}>
              {label}
            </Checkbox>
          ))}
        </Checkbox.Group>
      ) : (
        invalidType(type)
      )
    }
  />
);

const FormButton = ({ onClick, label, children }) => (
  <div className="button-container">
    <Form.Item label={label}>
      <Button type="primary" onClick={onClick}>
        {children}
      </Button>
    </Form.Item>
  </div>
);

const FormSubmit = ({ children }) => {
  const { isSubmitting } = useFormContext();

  return (
    <div className="button-container">
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={isSubmitting}>
          {children}
        </Button>
      </Form.Item>
    </div>
  );
};

const FormDebug = () => {
  const { errors, getValues } = useFormContext();

  const debug = (label, object) => (
    <div>
      <b>{label}</b>
      <pre>{JSON.stringify(object, null, 2)}</pre>
    </div>
  );

  return (
    <Card title="Form Debug Info">
      {debug('Form Values', getValues())}
      {debug('Form Errors', errors)}
    </Card>
  );
};

const useValidatedForm = (schema, schemaContext, initialValues) => {
  const { today } = useClientParams();

  const {
    control,
    handleSubmit,
    formState: { touchedFields, errors },
    watch,
    setError,
    setValue,
    getValues,
    trigger,
  } = useForm({
    defaultValues: initialValues,
    resolver: schema && yupResolver(schema),
    context: () => ({
      data: getValues(),
      ...schemaContext,
      today,
    }),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const clearValue = (name) => setValue(name, undefined);
  const clearValues = (names) => names.forEach(clearValue);
  const updateValue = (name, fn) => setValue(name, fn(getValues(name)));
  const clearValueIfEmptyString = (name) => {
    if (watch(name) === '') {
      clearValue(name);
    }
  };

  const validateTouchedFields = useCallback(() => {
    const touchedFieldNames = Object.keys(touchedFields ?? {});
    if (touchedFieldNames.length > 0) {
      trigger(touchedFieldNames);
    }
  }, [touchedFields, trigger]);

  return {
    control,
    errors,
    setError,
    watch,
    setValue,
    clearValue,
    clearValues,
    updateValue,
    clearValueIfEmptyString,
    getValues,
    handleSubmit,
    validateTouchedFields,
  };
};

const objectIdMap = new WeakMap();
const getObjectId = (object) => {
  if (!objectIdMap.has(object)) {
    objectIdMap.set(object, uuidv4());
  }
  return objectIdMap.get(object);
};

const ValidatedForm = ({ schema, schemaContext, initialValues, preValidation, handleSubmit, onFinish, children }) => {
  const methods = useValidatedForm(schema, schemaContext, initialValues);
  const { validateTouchedFields } = methods;
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    validateTouchedFields();
  }, [validateTouchedFields]);

  const { currentLanguage } = useLanguage();

  const getFieldDescribe = (schema, path) =>
    path
      .split('.')
      .reduce((s, key) => {
        const match = key.match(/^(\w+)\[(\d+)\]$/);
        return match ? s.fields[match[1]].innerType : s.fields[key];
      }, schema)
      ?.describe();

  const infoFor = (fieldName) => {
    const description = schema && getFieldDescribe(schema, fieldName);

    const translationsForField = description && schemaTranslations?.[description.label];
    const label = translationsForField?.label?.[currentLanguage];
    const placeholder = translationsForField?.placeholder?.[currentLanguage];

    const explainer = translationsForField?.explainerQuestion && {
      question: translationsForField?.explainerQuestion?.[currentLanguage],
      answer: translationsForField?.explainerAnswer?.[currentLanguage],
    };

    const required = description && description.tests?.some((test) => test.name === 'required');

    return { label, placeholder, explainer, required };
  };

  const { errors, watch, updateValue } = methods;

  const errorMessageFor = (fieldName, codeOrMessage) => {
    const description = schema && getFieldDescribe(schema, fieldName);
    const errorMessageLookedUp = schemaTranslations?.[description?.label]?.[codeOrMessage]?.[currentLanguage];

    return errorMessageLookedUp || codeOrMessage;
  };

  const errorFor = (fieldName) =>
    errorMessageFor(
      fieldName,
      fieldName.split('.').reduce((e, key) => {
        const match = key.match(/^(\w+)\[(\d+)\]$/);
        return match ? e?.[match[1]]?.[match[2]] : e?.[key];
      }, errors)?.message,
    );

  const iterateList = (name, forEach) => (
    <>
      {(watch(name) ?? []).map((object, index) => {
        const nameFor = (field) => `${name}[${index}].${field}`;
        const removeElementAtIndex = (array, index) => JSON.parse(JSON.stringify(array.filter((_, i) => i !== index)));
        const remove = () => updateValue(name, (array) => removeElementAtIndex(array, index));
        const element = forEach({ index, nameFor, remove });
        return element && cloneElement(element, { key: getObjectId(object) });
      })}
    </>
  );

  const addToList = (name, newValue) => updateValue(name, (array) => [...array, newValue]);

  const handleValidationsAndSubmit = async (values) => {
    try {
      setIsSubmitting(true);
      const result = await handleSubmit(values);
      onFinish && (await onFinish(result));
    } catch (error) {
      if (error instanceof ServerValidationError) {
        for (const { field, code } of error.errors) {
          methods.setError(field, { message: code });
        }
      } else throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const allMethods = { ...methods, iterateList, addToList };

  const renderOneChild = (child, key) => (typeof child === 'function' ? child(allMethods) : child);
  const renderedChildren = (Array.isArray(children) ? children : [children]).map(renderOneChild);
  const renderedChildrenWithKeys = renderedChildren.map((child, key) => child && cloneElement(child, { key }));

  const handleValidationAndSubmit = async () => {
    preValidation && (await preValidation(allMethods));
    return await methods.handleSubmit(handleValidationsAndSubmit)();
  };
  return (
    <FormProvider {...methods} infoFor={infoFor} errorFor={errorFor} isSubmitting={isSubmitting}>
      <Form layout="vertical" onFinish={handleValidationAndSubmit}>
        {renderedChildrenWithKeys}
      </Form>
    </FormProvider>
  );
};

export { FormField, FormInput, FormChoice, FormButton, FormSubmit, FormDebug, ValidatedForm };
